import { defineStore } from 'pinia'
import { computed, ref, toRaw, unref, watch } from 'vue'
import type { RoleMenuType } from '@/types/menu'
import type { List } from '@/types/common'
import { setCache, getCache } from '@/utils/cache/local'
import { getAuthMenuApi, getCurrentSystemList, getCurrentSystemMenu } from '@/apis/user'
import { formatOriginMenu, hiddenMenuList } from '@/router/utils'
import { cloneDeep } from 'lodash-es'
import { router } from '@/router'
import { getFlatArr, getLocationHash } from '@/utils/util'
import { ElMessageBox } from 'element-plus'
import { useNavHistory } from '@/stores/modules/routerNav'
import { whiteList } from '@/permission'
import { recursion } from '@/utils/tools'
import { useAccountStore } from '@/stores/modules/account'
import { hasCode } from '@/utils'
import { useOrgInfoStore } from './orgInfo'
import { getHashParam } from '@/utils/tools'
import { useGlobalStore } from '@/stores/modules/global'

interface SystemType extends List {
  menuList: RoleMenuType[]
}

function tempPromise() {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(true)
    }, 10)
  })
}

export const usePermissionStore = defineStore('usePermission', () => {
  const systemListData = ref<SystemType[]>([])
  const menuList = ref<RoleMenuType[]>([])
  const allMenuList = ref<RoleMenuType[]>([])
  const predefineAppIdMap = ref<Recordable>({})
  const status = ref(false)
  // 当前系统code
  const currentSys = ref(getHashParam('sysCode') || getCache('currentSys') || '')
  const currentMenu: any = ref({})

  const resourceList: any = computed(() => {
    return unref(currentMenu).resourceList
  })

  // 接口请求头header需要
  const headerMenuId = ref('')
  const headerTableCode = ref('')
  const headerAppCode = ref('')

  // 用于本身没有按钮权限，但需要继承其他页面的按钮权限的页面
  const resourceListExtend: any = ref([])

  watch(resourceList, list => {
    setResourceListExtend(toRaw(list) as any[])
  })

  function setHeaderMenuId(id: string) {
    headerMenuId.value = id
  }

  function setMenuAppCode(id: string) {
    headerAppCode.value = id
  }

  function setHeaderTableCode(id: string) {
    headerTableCode.value = id
  }

  function setCurrentMenu(menu: any) {
    currentMenu.value = menu
  }

  function setCurrentSys(code: string) {
    currentSys.value = code
    // 持久化
    setCache('currentSys', code)
    if (code === 'CRM') {
      // 如果是切换到CRM客户管理，就更新一下员工派单开关信息
      useAccountStore()?.getStaffDistributeSwitchInfo?.()
    }
  }

  function initPredefineAppIdMap(routes: any[]) {
    const obj: Recordable = {}
    recursion(routes, menu => {
      if (menu.defineType === 10 && !obj[menu.id]) {
        obj[menu.id] = `/${menu.path}`
      }
    })
    predefineAppIdMap.value = obj
  }

  function getPredefineAppId(path: string) {
    let menuId = ''
    for (const key in unref(predefineAppIdMap)) {
      if (unref(predefineAppIdMap)[key] === path) {
        menuId = key
      }
    }
    return menuId
  }

  async function getSystemList() {
    try {
      const res = await getCurrentSystemList()
      // 将工作台放在第一位
      if (res?.length) {
        res?.forEach((item: Recordable) => (item.code === 'WB' ? (item.sort = 0) : (item.sort = 1)))
        res?.sort((a: Recordable, b: Recordable) => a.sort - b.sort)
      }
      let systemMenu: any[] = []
      try {
        systemMenu = await getCurrentSystemMenu() // 获取所有子系统的菜单

        const orgInfoStore = useOrgInfoStore()
        const isHasWechat = hasCode(systemMenu?.EMB || [], 'WechatManage')
        if (isHasWechat) {
          const accountStore = useAccountStore()
          const userInfo = accountStore.userInfo
          orgInfoStore.fetchWxConfig(userInfo?.tenantId)
        }
        // 财智通资质（功能下线：getSysParamByCode/get_flair_config_key 网关不可用，暂不请求）
        // systemMenu?.DIS && (await orgInfoStore.fetchSpecialize())
      } catch (e) {
        console.log(e)
      }
      systemListData.value = res.map((item: any) => {
        return {
          ...item,
          menuList: formatOriginMenu(cloneDeep(systemMenu[item.code]) || [], item.code)
        }
      })

      // 全量菜单赋值
      if (systemMenu) {
        let list: any[] = []
        let routes: any[] = []
        for (const code in systemMenu) {
          list = list.concat(formatOriginMenu(cloneDeep(systemMenu[code]) || [], code))
          routes = routes.concat(cloneDeep(systemMenu[code]) || [])
        }
        allMenuList.value = list
        initPredefineAppIdMap(routes)
      }

      // console.log(
      //   '%c [systemListData  ]-134',
      //   'font-size:13px; background:pink; color:#bf2c9f;',
      //   allMenuList
      // )

      let path = getLocationHash()
      status.value = true
      if (systemListData.value?.length) {
        // 默认取第一个有菜单的子系统
        const hasMenuSystems = unref(systemListData).filter(sys => sys.menuList?.length)
        let code: string = hasMenuSystems[0]?.code || unref(systemListData)[0]?.code
        const navHistory = useNavHistory()
        if (whiteList.includes(path.split('?')[0])) {
          path = ''
        }
        // 在工作机页面刷新，要保持还在工作机页面
        if (path.split('?')[0] === '/workerManage') {
          router.push('/workerManage')
          return false
        }
        // 有缓存子系统就走缓存
        const system = systemListData.value.find(sys => sys.code === currentSys.value)
        if (system) {
          code = currentSys.value
        }
        // 有对应路由的跳转对应的系统
        const pathSystem = systemListData.value.find(sys => sys.code === getCurrentSystemCode(path))
        if (pathSystem) {
          code = pathSystem.code
        }
        navHistory.setActiveSysCode(code)
        // 有path时根据path选择跳转的系统，有缓存时根据缓存跳转系统，都没有就跳转第一个有菜单的系统
        await changeSystem(code, path)
      } else {
        router.push('/502')
        await ElMessageBox.alert('您无权限访问任何子系统，请联系管理员添加！', '无可访问子系统')
        // 没有子系统就退出登录
        throw Error('无可访问子系统')
      }
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  /**
   * 切换系统以及对应的菜单
   * @param systemCode  系统code
   * @param path 要跳转的路由或当前的路由
   * @param jump 是否跳转
   */
  async function changeSystem(systemCode: string, path: string = '', jump = true) {
    try {
      // 当切换系统时清空一下菜单，不然不会默认展开active的菜单组
      if (currentSys.value !== systemCode) {
        menuList.value = []
      }
      setCurrentSys(systemCode)
      // 根据子系统拉取左侧菜单
      const system: any = unref(systemListData).find((el: any) => el.code === systemCode) || {}
      if (system?.menuList) {
        await tempPromise()
        menuList.value = system.menuList
      } else {
        const res = await getAuthMenuApi({ systemCode: systemCode })
        menuList.value = formatOriginMenu(cloneDeep(res) || [], systemCode)
        system.menuList = [...menuList.value]
      }
      if (!jump) return
      // 菜单跳转逻辑
      if (menuList.value.length) {
        const flatArr: any[] = getFlatArr(unref(menuList) as any)
        const res = flatArr.find(menu => menu.path === path?.split('?')?.[0])
        const isDetail = hiddenMenuList.find(value => path?.split('?')?.[0]?.indexOf(value) > -1) // 是否为详情页
        if (res || isDetail) {
          goPage({
            name: res?.name,
            path,
            systemCode: res?.systemCode || systemCode,
            fullPath: path
          })
        } else if (useGlobalStore()?.frameCode) {
          // 内嵌的页面 如果没有对应菜单权限就跳502
          await ElMessageBox.alert('无此菜单访问权限，请联系管理员添加！', '无访问权限')
          await router.push('/502')
        } else {
          // menuList中没有path就跳第一个菜单(详情页面不能判断) 隐藏的菜单不能跳
          const flatArr: any[] = getFlatArr(unref(menuList) as any)
          for (let i = 0; i < flatArr.length; i++) {
            // 如果没有children或children的第0项没有children且showed为false,则展示
            const children = flatArr[i]?.children
            if (children?.length) {
              if (children[0].showed === false) {
                goPage(flatArr[i])
                break
              }
            } else if (flatArr[i].showed !== false) {
              goPage(flatArr[i])
              break
            }
          }
        }
      } else {
        // 提示一下
        // await router.push('/502')
        await ElMessageBox.alert('该子系统下没有可访问的菜单，请联系管理员添加！', '无可访问菜单')
        throw new Error('无可访问菜单')
      }
    } catch (e) {
      console.log(e)
    }
  }

  function goPage(menu: Recordable) {
    console.log('%c [ menu ]-221', 'font-size:13px; background:#00e8e4; color:#44ffff;', menu)
    const navHistory = useNavHistory()
    navHistory.addHistory({
      name: menu.name,
      path: menu.path.split('?')[0],
      fullPath: menu.fullPath,
      closeAble: false,
      systemCode: menu.systemCode
    })
    router.push(menu.path)
  }

  function setResourceListExtend(list: any[] | undefined = undefined) {
    resourceListExtend.value = list || unref(resourceList)
  }

  function getCurrentSystemCode(path: string) {
    path = path.split('?')[0]
    const res = allMenuList.value.find(menu => menu.path === path)
    return res?.systemCode
  }

  // 清除系统相关数据 退出登录调用
  function clearCache() {
    systemListData.value = []
    allMenuList.value = []
    status.value = false
    currentMenu.value = {}
    setCurrentSys('')
  }

  return {
    getSystemList,
    systemListData,
    menuList,
    allMenuList,
    changeSystem,
    currentSys,
    status,
    resourceList,
    resourceListExtend,
    currentMenu,
    setResourceListExtend,
    setCurrentMenu,
    setCurrentSys,
    clearCache,
    headerMenuId,
    setHeaderMenuId,
    getPredefineAppId,
    setHeaderTableCode,
    headerTableCode,
    setMenuAppCode,
    headerAppCode
  }
})

interface PermissionManageState {
  roleId: string
  subSystemCode: string
  menuId: string
  menuIndex: number
  disabledAll: boolean
  isSuper: boolean
  isEditMenu: boolean
  refreshFlag: boolean
}

export const usePermissionManageStore = defineStore('PermissionManage', {
  state: (): PermissionManageState => ({
    roleId: '',
    menuId: '',
    subSystemCode: '',
    menuIndex: 0,
    disabledAll: false,
    isSuper: false,
    isEditMenu: false,
    refreshFlag: false
  }),
  actions: {
    setRoleId(id: string) {
      this.roleId = id
      this.setIsEditMenu(false)
    },
    setIsEditMenu(bool: boolean) {
      this.isEditMenu = bool
    },
    setMenuIndex(index: number) {
      this.menuIndex = index
    },
    setMenuId(id: string) {
      this.menuId = id
    },
    setSubSystemCode(code: string) {
      this.subSystemCode = code
    },
    setIsSuper(role: any) {
      this.isSuper = role?.roleType?.name === 'SUPER' || role.autoAuth
    },
    setRefreshFlag(bool: any) {
      this.refreshFlag = bool
    }
  }
})
